import { useState } from "react";
import { X, Loader2, Wallet } from "lucide-react";
import { useTranslation } from "react-i18next";
import { fmtUsd } from "@/lib/wallet";

const API = process.env.REACT_APP_BACKEND_URL;

export default function RoomJoinModal({ open, room, onClose, onJoined, balance }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  if (!open || !room) return null;

  const canPay = balance != null && balance >= room.entry_fee_cents;

  const join = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await fetch(`${API}/api/rooms/${room.room_id}/join`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || t("room_join.generic_error"));
      onJoined?.(d);
    } catch (e) {
      setErr(e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md flex items-center justify-center p-4" data-testid="room-join-modal">
      <div className="w-full max-w-md rounded-sm border border-primary/40 bg-gradient-to-b from-[#050a18] to-[#020408] p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-mono text-[10px] tracking-[0.3em] text-secondary">{t("room_join.title_prefix")}</p>
            <h2 className="font-display font-black text-2xl tracking-tighter text-white">{room.label}</h2>
          </div>
          <button onClick={onClose} data-testid="room-join-close" className="p-2 border border-primary/30 rounded-sm hover:bg-primary/10 text-primary">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="grid grid-cols-3 gap-2 text-center font-mono text-[10px]">
          <div className="border border-primary/20 rounded-sm py-2">
            <div className="text-muted-foreground">{t("room_join.entry_label")}</div>
            <div className="text-primary font-bold text-sm">{fmtUsd(room.entry_fee_cents)}</div>
          </div>
          <div className="border border-primary/20 rounded-sm py-2">
            <div className="text-muted-foreground">{t("room_join.players_label")}</div>
            <div className="text-primary font-bold text-sm">{room.player_count}/{room.capacity}</div>
          </div>
          <div className="border border-primary/20 rounded-sm py-2">
            <div className="text-muted-foreground">{t("room_join.duration_label")}</div>
            <div className="text-primary font-bold text-sm">60min</div>
          </div>
        </div>

        <div className="text-xs font-mono text-muted-foreground border border-primary/20 rounded-sm p-3 space-y-1">
          <p>{t("room_join.rules_min")}</p>
          <p>{t("room_join.rules_fee")}</p>
          <p>{t("room_join.rules_split")}</p>
          <p>{t("room_join.rules_best")}</p>
        </div>

        {/* New wallet-flow explainer */}
        <div className="rounded-sm border border-emerald-500/40 bg-emerald-500/5 p-3 text-xs font-mono text-emerald-200 flex items-start gap-2" data-testid="room-join-wallet-notice">
          <Wallet className="h-4 w-4 mt-0.5 shrink-0" />
          <div className="space-y-1">
            <p className="text-emerald-300 font-bold tracking-widest">PRÊMIOS INSTANTÂNEOS</p>
            <p>Vitórias caem direto na sua carteira interna. Para sacar em USDT, use o botão SACAR na /carteira.</p>
          </div>
        </div>

        {!canPay && (
          <div className="rounded-sm border border-amber-500/50 bg-amber-500/10 p-3 text-xs text-amber-300 font-mono">
            {t("room_join.insufficient", { have: fmtUsd(balance), need: fmtUsd(room.entry_fee_cents) })}
          </div>
        )}
        {err && <div className="rounded-sm border border-red-500/50 bg-red-500/10 p-3 text-xs text-red-300 font-mono">{err}</div>}

        <button onClick={join} disabled={busy || !canPay}
          data-testid="room-join-confirm"
          className="w-full inline-flex items-center justify-center gap-2 rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 font-mono tracking-widest text-xs px-5 py-3 disabled:opacity-40">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : `${t("room_join.confirm_prefix")}${fmtUsd(room.entry_fee_cents)} ▶`}
        </button>
      </div>
    </div>
  );
}
